// Mock dataset and helpers for Email Notifications (Phase 0)
// This file generates deterministic mock records and provides filtering/sorting/pagination utilities

const STATUSES = ["queued", "sent", "failed", "delivered"];
const STATUS_DISPLAY = ["Queued", "Sent", "Failed", "Delivered"];
const CONTEXTS = ["TRAINING", "SIGNUP", "PASSWORD_RESET", "ONBOARDING", "OTHER"];
const SEND_MODES = ["initial", "reminder", "system"];

const seedRandom = (seed) => {
  let t = seed;
  return () => {
    // Xorshift32
    t ^= t << 13; // eslint-disable-line
    t ^= t >>> 17; // eslint-disable-line
    t ^= t << 5; // eslint-disable-line
    return ((t < 0 ? ~t + 1 : t) % 10000) / 10000;
  };
};

const rng = seedRandom(123456);

const pick = (arr) => arr[Math.floor(rng() * arr.length) % arr.length];

const names = [
  "Alex Tan", "Sam Lee", "Jordan Lim", "Priya Singh", "Marcus Ong",
  "Wei Ling", "Derrick Koh", "Qiao Yu", "Aman Shah", "Sayang Nur"
];

const domains = ["example.com", "demo.io", "zavior.app", "mail.test"];

const organizations = {
  "org-1": "ALPHA One Solution",
  "org-2": "Beta Corporation",
  "org-3": "Gamma Enterprises"
};

const randomEmail = (name) => {
  const user = name.toLowerCase().replace(/[^a-z]/g, '.') + Math.floor(rng() * 100);
  return `${user}@${pick(domains)}`;
};

const randomDateInPastDays = (days) => {
  const now = Date.now();
  const delta = Math.floor(rng() * days * 24 * 60 * 60 * 1000);
  return new Date(now - delta).toISOString();
};

const makeRecord = (i) => {
  const fullName = pick(names);
  const status = pick(STATUSES);
  const context_type = pick(CONTEXTS);
  const to_email = randomEmail(fullName);
  const template_name = context_type === "TRAINING" ? "MANDATORY_CSA_TRAINING_TRIGGER" : `TEMPLATE_${context_type}`;
  const template_id = `tmpl_${context_type}_${(1000 + i).toString(36)}`;
  const provider_message_id = `msg_${(100000 + i).toString(36)}`;
  const created_at = randomDateInPastDays(120);
  const organization_id = rng() > 0.15 ? `org-${(1 + (i % 3))}` : null; // some global events
  const organization_name = organization_id ? organizations[organization_id] : null;

  return {
    id: `mock-${i}`,
    created_at,
    organization_id,
    organization_name,
    context_type,
    context_id: context_type === "TRAINING" ? `training-${i % 7}` : null,
    to_email,
    to_name: fullName,
    from_email: "no-reply@zavior.app",
    template_name,
    template_id,
    send_mode: pick(SEND_MODES),
    status,
    status_code: status === "failed" ? 500 : 200,
    error_message: status === "failed" ? "Mock transient failure" : null,
    provider_message_id,
    correlation_id: `corr_${(5000 + i).toString(36)}`,
    response_excerpt: status === "failed" ? "Email service unavailable" : "Accepted by provider",
    request_payload: {
      template_id,
      template_name,
      from: { email: "no-reply@zavior.app", name: "Zavior" },
      personalizations: [
        {
          to: [{ email: to_email, name: fullName }],
          dynamic_template_data: {
            full_name: fullName,
            training_name: context_type === "TRAINING" ? "Cybersecurity Training (by CSA)" : undefined,
            start_date: context_type === "TRAINING" ? "2025-07-29" : undefined,
            end_date: context_type === "TRAINING" ? "2025-08-16" : undefined
          }
        }
      ]
    }
  };
};

export const MOCK_EMAIL_LOGS = Array.from({ length: 240 }).map((_, i) => makeRecord(i + 1));

// Helper: filter/sort/paginate locally to mimic server behaviour
export const queryEmailLogs = ({
  q = "",
  status = [],
  contextType = [],
  from,
  to,
  page = 1,
  pageSize = 25,
  organizationId
} = {}) => {
  let data = [...MOCK_EMAIL_LOGS];

  // text search
  const query = (q || "").trim().toLowerCase();
  if (query) {
    data = data.filter(r => (
      r.to_email.toLowerCase().includes(query) ||
      (r.to_name || "").toLowerCase().includes(query) ||
      (r.template_name || "").toLowerCase().includes(query) ||
      (r.organization_name || "").toLowerCase().includes(query)
    ));
  }

  // status filter
  if (Array.isArray(status) && status.length) {
    const set = new Set(status);
    data = data.filter(r => set.has(r.status));
  }

  // context filter
  if (Array.isArray(contextType) && contextType.length) {
    const set = new Set(contextType);
    data = data.filter(r => set.has(r.context_type));
  }

  // org filter
  if (organizationId) {
    data = data.filter(r => r.organization_id === organizationId);
  }

  // date range
  if (from) {
    const t = new Date(from).getTime();
    data = data.filter(r => new Date(r.created_at).getTime() >= t);
  }
  if (to) {
    const t = new Date(to).getTime();
    data = data.filter(r => new Date(r.created_at).getTime() <= t);
  }

  // sort by created_at desc
  data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const total = data.length;
  const start = (Math.max(1, page) - 1) * Math.max(1, pageSize);
  const end = start + Math.max(1, pageSize);
  const items = data.slice(start, end);

  return { items, total, page, pageSize };
};

export const STATUS_OPTIONS = STATUSES.map((s, i) => ({ value: s, label: STATUS_DISPLAY[i] }));
export const CONTEXT_OPTIONS = CONTEXTS;


